import { getApiBase } from '@/lib/apiBase';

const API_BASE = getApiBase();
const TIMEOUT_MS = Number(process.env.REACT_APP_CHAT_TIMEOUT_MS || 20000);

/**
 * Once the server says the assistant is off, stop calling it for the rest of the tab so a
 * misconfigured deployment does not fire a doomed request on every unmatched question.
 */
let disabledForSession = false;

export function isChatApiDisabled() {
  return disabledForSession;
}

/**
 * Live plan prices and whether the LLM fallback is available. Failing quietly is fine: the widget
 * falls back to a price-free answer and to canned-only behaviour.
 *
 * @returns {Promise<{ llmEnabled: boolean, plans: object[] }>}
 */
export async function fetchChatConfig() {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), 8000);
  try {
    const res = await fetch(`${API_BASE}/api/chat/config`, { signal: controller.signal });
    if (!res.ok) return { llmEnabled: false, plans: [] };
    const data = await res.json();
    if (!data.llmEnabled) disabledForSession = true;
    return { llmEnabled: Boolean(data.llmEnabled), plans: Array.isArray(data.plans) ? data.plans : [] };
  } catch {
    return { llmEnabled: false, plans: [] };
  } finally {
    window.clearTimeout(timer);
  }
}

/**
 * @param {{ message: string, audience: string, history?: {role: string, text: string}[] }} input
 * @returns {Promise<{ ok: true, answer: string, suggestEscalation: boolean } | { ok: false, code: string }>}
 */
export async function askAssistant({ message, audience, history = [] }) {
  if (disabledForSession) return { ok: false, code: 'disabled' };

  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const res = await fetch(`${API_BASE}/api/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message, audience, history }),
      signal: controller.signal,
    });

    if (res.status === 503) {
      disabledForSession = true;
      return { ok: false, code: 'disabled' };
    }
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      return { ok: false, code: data.code || 'error' };
    }

    const data = await res.json();
    const answer = String(data.answer || '').trim();
    if (!answer) return { ok: false, code: 'empty' };
    return { ok: true, answer, suggestEscalation: Boolean(data.suggestEscalation) };
  } catch (err) {
    return { ok: false, code: err?.name === 'AbortError' ? 'timeout' : 'network' };
  } finally {
    window.clearTimeout(timer);
  }
}
